import * as React from "react"
import { Head, Link } from "@inertiajs/react"
import { ChevronRight } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Select } from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"
import type { AutomationReportProps, AutomationReportRow } from "@/types/automation_report"

const TITLE = "Automation report"
const DESCRIPTION =
  "Every automation running across IUPAT: which organization, Rehearsal or Live, how many members the last run messaged, and live texts sent by week."

type ModeFilter = "all" | "live" | "rehearsal"

export default function AutomationReport({
  rows,
  totals,
  by_organization,
  weeks,
  weekly,
  organizations,
  zone_label,
  can_switch_scope,
}: AutomationReportProps) {
  const [organization, setOrganization] = React.useState("")
  const [mode, setMode] = React.useState<ModeFilter>("all")

  const visible = rows.filter(
    (row) =>
      (organization === "" || row.organization === organization) &&
      (mode === "all" || (mode === "live") === !row.is_rehearsal),
  )

  const weeklyTotals = weeks.map((_, i) => weekly.reduce((sum, row) => sum + row.counts[i], 0))

  return (
    <>
      <Head title={TITLE}>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <PageHeader
          title="Automation Report"
          description="What is running, where, and what it has sent. Message counts are live sends only; rehearsals send nothing."
        />

        <div className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatTile label="Running automations" value={totals.running} />
          <StatTile label="Live" value={totals.live} />
          <StatTile label="In rehearsal" value={totals.rehearsal} />
          <StatTile label="Texts sent this week" value={totals.sent_this_week} />
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-2">
          <section className="space-y-3">
            <div>
              <h2>Running by organization</h2>
              {totals.organizations > 0 && (
                <p className="mt-1 text-sm text-ink-muted">
                  {totals.organizations === 1
                    ? "1 organization has an automation switched on."
                    : `${totals.organizations} organizations have an automation switched on.`}
                </p>
              )}
            </div>
            <BarList rows={by_organization} />
          </section>

          <section className="space-y-3">
            <div>
              <h2>Texts sent by week</h2>
              <p className="mt-1 text-sm text-ink-muted">
                Live sends across every organization over the last {weeks.length} weeks.
                Weeks start on Monday, {zone_label}.
              </p>
            </div>
            <WeeklyColumns weeks={weeks} totals={weeklyTotals} />
          </section>
        </div>

        <section className="mt-10 space-y-3">
          <div>
            <h2>Texts sent by week, by organization</h2>
            <p className="mt-1 text-sm text-ink-muted">
              Darker cells sent more. Organizations that sent nothing in these weeks are left
              out.
            </p>
          </div>
          <WeeklyGrid weeks={weeks} weekly={weekly} />
        </section>

        <section className="mt-10 space-y-4">
          <div>
            <h2>Running automations</h2>
            <p className="mt-1 text-sm text-ink-muted">
              Every setup switched on, of an automation that is active.
            </p>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label htmlFor="report_organization">Organization</label>
              <Select
                id="report_organization"
                wrapperClassName="w-64 max-w-full"
                value={organization}
                onChange={(event) => setOrganization(event.target.value)}
              >
                <option value="">All organizations</option>
                {organizations.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <label htmlFor="report_mode">Mode</label>
              <Select
                id="report_mode"
                wrapperClassName="w-44"
                value={mode}
                onChange={(event) => setMode(event.target.value as ModeFilter)}
              >
                <option value="all">Live and rehearsal</option>
                <option value="live">Live only</option>
                <option value="rehearsal">Rehearsal only</option>
              </Select>
            </div>
          </div>

          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Automation</TableHeaderCell>
                <TableHeaderCell>Organization</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>Mode</TableHeaderCell>
                <TableHeaderCell>Last run</TableHeaderCell>
                <TableHeaderCell numeric>Members messaged</TableHeaderCell>
                <TableHeaderCell>
                  <span className="sr-only">Open</span>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 ? (
                <TableEmpty colSpan={7}>
                  {rows.length === 0
                    ? "Nothing is running. A setup appears here once it is switched on."
                    : "Nothing running matches these filters."}
                </TableEmpty>
              ) : (
                visible.map((row) => (
                  <ReportRow key={row.id} row={row} canSwitchScope={can_switch_scope} />
                ))
              )}
            </TableBody>
          </Table>
        </section>
      </AdminShell>
    </>
  )
}

function ReportRow({ row, canSwitchScope }: { row: AutomationReportRow; canSwitchScope: boolean }) {
  const run = row.last_run

  return (
    <TableRow>
      <TableCell>
        <span className="font-medium text-ink-display">{row.automation}</span>
        {row.label && <span className="block text-xs text-ink-muted">{row.label}</span>}
        <span className="block text-xs text-ink-muted">{row.schedule_label}</span>
      </TableCell>
      <TableCell>
        {row.organization}
        {row.council && <span className="block text-xs text-ink-muted">{row.council}</span>}
      </TableCell>
      <TableCell nowrap>{row.channel}</TableCell>
      <TableCell nowrap>
        {row.is_rehearsal ? <Badge tone="signal">Rehearsal</Badge> : <Badge tone="accent">Live</Badge>}
      </TableCell>
      <TableCell nowrap>
        {run ? (
          <Link href={`/runs/${run.id}`}>{run.started_at_label}</Link>
        ) : (
          <span className="text-ink-muted">Not run yet</span>
        )}
        {run?.was_dry_run && <span className="block text-xs text-ink-muted">Rehearsal</span>}
      </TableCell>
      <TableCell numeric>
        {run ? (
          run.was_dry_run ? (
            <span title="A rehearsal sends nothing; this many would have been texted.">
              0 <span className="text-xs text-ink-muted">({run.sent_count.toLocaleString()} would)</span>
            </span>
          ) : (
            run.sent_count.toLocaleString()
          )
        ) : (
          "—"
        )}
      </TableCell>
      <TableCell nowrap>
        {canSwitchScope && (
          // Setup pages show the organization being viewed, so this switches to
          // the row's organization and then opens the setup.
          <Link
            href="/scope"
            method="patch"
            as="button"
            data={{ organization_id: row.organization_id, return_to: row.setup_path }}
            className="inline-flex items-center gap-1 text-sm text-accent-display hover:underline"
            aria-label={`Open ${row.automation} for ${row.organization}`}
          >
            Open
            <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        )}
      </TableCell>
    </TableRow>
  )
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-hairline bg-surface p-4">
      <p className="text-xs text-ink-muted">{label}</p>
      <span className="mt-1 block text-2xl font-semibold tabular-nums text-ink-display">
        {value.toLocaleString()}
      </span>
    </div>
  )
}

/** One bar per organization, longest first. A single series, so no legend. */
function BarList({ rows }: { rows: AutomationReportProps["by_organization"] }) {
  if (rows.length === 0) {
    return <p className="text-sm text-ink-muted">Nothing is running.</p>
  }

  const max = Math.max(...rows.map((row) => row.running))

  return (
    <ul className="m-0 list-none space-y-2 p-0">
      {rows.map((row) => (
        <li
          key={row.organization}
          className="grid grid-cols-[minmax(0,9rem)_1fr] items-center gap-3 text-sm"
          title={`${row.organization}: ${row.running} running, ${row.live} live`}
        >
          <span className="truncate text-ink-body">{row.organization}</span>
          <span className="flex items-center gap-2">
            <span
              className="h-3 rounded-r bg-accent"
              style={{ width: `${Math.max((row.running / max) * 100, 2)}%` }}
            />
            <span className="shrink-0 tabular-nums text-xs text-ink-muted">{row.running}</span>
          </span>
        </li>
      ))}
    </ul>
  )
}

/** Weekly totals as columns. The grid below is the same data as a table. */
function WeeklyColumns({
  weeks,
  totals,
}: {
  weeks: AutomationReportProps["weeks"]
  totals: number[]
}) {
  const max = Math.max(...totals, 0)

  if (max === 0) {
    return <p className="text-sm text-ink-muted">No live texts sent in these weeks.</p>
  }

  const last = totals.length - 1

  return (
    <div>
      <div className="flex h-40 items-end gap-0.5 border-b border-hairline" role="img"
        aria-label={`Texts sent per week: ${weeks.map((w, i) => `${w.label} ${totals[i]}`).join(", ")}`}
      >
        {totals.map((total, i) => (
          <div
            key={weeks[i].starts_on}
            className="group flex h-full flex-1 flex-col items-center justify-end"
            title={`Week of ${weeks[i].label}: ${total.toLocaleString()} sent`}
          >
            {(i === last || total === max) && (
              <span className="mb-1 text-[11px] tabular-nums text-ink-muted">
                {total.toLocaleString()}
              </span>
            )}
            <div
              className="w-full max-w-6 rounded-t bg-accent group-hover:opacity-80"
              style={{ height: `${total === 0 ? 0 : Math.max((total / max) * 100, 2)}%` }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-ink-muted">
        <span>{weeks[0].label}</span>
        <span>{weeks[last].label}</span>
      </div>
    </div>
  )
}

/** Organizations by week, shaded by count on one hue: darker sent more. */
function WeeklyGrid({
  weeks,
  weekly,
}: {
  weeks: AutomationReportProps["weeks"]
  weekly: AutomationReportProps["weekly"]
}) {
  const max = Math.max(...weekly.flatMap((row) => row.counts), 0)

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell sticky>Organization</TableHeaderCell>
          {weeks.map((week) => (
            <TableHeaderCell key={week.starts_on} numeric>
              {week.label}
            </TableHeaderCell>
          ))}
          <TableHeaderCell numeric>Total</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {weekly.length === 0 ? (
          <TableEmpty colSpan={weeks.length + 2}>No live texts sent in these weeks.</TableEmpty>
        ) : (
          weekly.map((row) => (
            <TableRow key={row.organization}>
              <TableCell sticky nowrap>
                {row.organization}
              </TableCell>
              {row.counts.map((count, i) => (
                <TableCell
                  key={weeks[i].starts_on}
                  numeric
                  title={`${row.organization}, week of ${weeks[i].label}: ${count.toLocaleString()} sent`}
                  style={
                    count > 0
                      ? {
                          backgroundColor: `color-mix(in oklch, var(--color-accent) ${Math.round(
                            12 + (count / max) * 48,
                          )}%, transparent)`,
                        }
                      : undefined
                  }
                >
                  {count > 0 ? count.toLocaleString() : <span className="text-ink-muted">·</span>}
                </TableCell>
              ))}
              <TableCell numeric>
                <span className="font-medium text-ink-display">{row.total.toLocaleString()}</span>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  )
}
